/* eslint-disable max-lines */
import { ConnectionPositionPair } from '@angular/cdk/overlay';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';

import { Store } from '@ngrx/store';
import { distinctUntilKeyChanged, merge, Subject } from 'rxjs';
import { first, switchMap, takeUntil } from 'rxjs/operators';

import { Ability } from '@recruitee/abilities-types';
import {
  SHARE_ON_THE_GO_I18N,
  ShareOnTheGoDialogResult,
  ShareOnTheGoI18n,
  ShareOnTheGoService,
} from '@recruitee/access-to-candidate';
import type { User } from '@recruitee/account-types';
import { AddEvaluationDropdownComponent } from '@recruitee/add-evaluations-dropdown';
import { QuickEvaluationContext } from '@recruitee/analytics';
import type { FullCandidate, ShareOnTheGoRequestPayload } from '@recruitee/candidate-types';
import { filterBoolean } from '@recruitee/common';
import { I18nService, TranslatePipe } from '@recruitee/i18n';
import { InterviewResultRequestActions } from '@recruitee/interview-result-request-data';
import { InterviewResultsActions } from '@recruitee/interview-results-data';
import type { InterviewResult } from '@recruitee/interview-results-types';
import {
  InterviewTemplateScope,
  InterviewTemplateSelectors,
} from '@recruitee/interview-templates-data';
import type { InterviewTemplate } from '@recruitee/interview-templates-types';
import { AppNavigatorService } from '@recruitee/navigation';
import { OfferSelectOptionHelper, PlacementOfferSelectOption } from '@recruitee/offers-ui';
import type { Placement } from '@recruitee/placements-types';
import { EvaluationsAnalytics, WorkflowTemplatesAnalytics } from '@recruitee/temp';
import { HiddenScoreIconComponent } from '@recruitee/unbiased-hiring';
import {
  ConnectedOverlayDirective,
  OverlayOriginDirective,
  RtStatefulComponent,
  SelectGroupedOptions,
  SelectGroupOption,
  HeadingComponent,
  InterviewEvaluationRatingComponent,
  IconComponent,
  IconColorDirective,
  ButtonComponent,
  GroupComponent,
} from '@recruitee/user-interface';
import { generateUuid, sortByKey } from '@recruitee/utils';
import { WorkflowTemplatesFeatures } from '@recruitee/workflow-templates-types';

import { AddQuickEvaluationDropdownComponent } from '../add-quick-evaluation-dropdown/add-quick-evaluation-dropdown.component';
import { InterviewKind } from '../interview-kind';

type State = {
  templates: SelectGroupedOptions[];
  offers: PlacementOfferSelectOption[];
  hover: boolean;
  myLastRating: string | null;
};

@Component({
  selector: 'rt-add-evaluation',
  templateUrl: 'add-evaluation.component.html',
  styleUrls: ['./add-evaluation.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    WorkflowTemplatesAnalytics,
    EvaluationsAnalytics,
    {
      provide: SHARE_ON_THE_GO_I18N,
      useValue: <ShareOnTheGoI18n>{
        submitBtn: 'app.candidate.share_on_the_go.allow_access_to_candidate.request_evaluation',
        dontRequestOption:
          'app.candidate.share_on_the_go.allow_access_to_candidate.dont_request_evaluation_from',
        dontRequestOptionTooltip:
          'app.candidate.share_on_the_go.allow_access_to_candidate.skip_user_tooltip',
      },
    },
  ],
  standalone: true,
  imports: [
    OverlayOriginDirective,
    GroupComponent,
    ButtonComponent,
    IconColorDirective,
    IconComponent,
    InterviewEvaluationRatingComponent,
    HeadingComponent,
    HiddenScoreIconComponent,
    ConnectedOverlayDirective,
    AddEvaluationDropdownComponent,
    AddQuickEvaluationDropdownComponent,
    TranslatePipe,
  ],
})
export class AddEvaluationComponent extends RtStatefulComponent<State> {
  @Input() public candidate: FullCandidate;
  @Input() public placement?: Placement;
  @Input() public showEvaluation: boolean = false;
  @Input() public hasBorder: boolean;
  @Input() public disabled: boolean;
  @Input() public allowedUsers: User[];
  @Input() public hasQuickEvaluationDropdown: boolean = false;
  @Input() public isActionToolbar: boolean = false;
  @Input() public evalDropdownOrigin: OverlayOriginDirective;
  @Input() public hasTriggerContent: boolean = true;
  @Input() public currentUserAvailablePlacements: Placement[] = [];
  @Input() public hasAccessToAllJobOffers: boolean = false;
  @Input() public quickEvaluationTrigger: QuickEvaluationContext;
  @Input() public hasNewCandidateProfile: boolean = false;

  @Output() public optionsClick: EventEmitter<{
    expandQuickEvaluationForm: boolean;
    selectedOffer?: number;
  }> = new EventEmitter();
  @Output() public evalDropdownOpen: EventEmitter<void> = new EventEmitter();
  @Output() public evalDropdownClose: EventEmitter<void> = new EventEmitter();

  @ViewChild('evalDropdown', { static: false })
  public evalDropdownTpl: ConnectedOverlayDirective;
  @ViewChild('defaultEvalDropdownTrigger', { static: false })
  public defaultEvalDropdownTrigger: OverlayOriginDirective;

  public evaluationScoreCopy: string;

  public readonly quickDropdownPositions: ConnectionPositionPair[] = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' },
    { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top' },
  ];

  private ngOnDestroy$ = new Subject<void>();

  constructor(
    protected cdRef: ChangeDetectorRef,
    private appNavigator: AppNavigatorService,
    private i18n: I18nService,
    private store: Store,
    private analyticsWorkflowTemplates: WorkflowTemplatesAnalytics,
    private evaluationsAnalytics: EvaluationsAnalytics,
    private shareOnTheGoService: ShareOnTheGoService,
  ) {
    super(cdRef);
    this.createState({
      templates: [],
      offers: [],
      hover: false,
      myLastRating: null,
    });
  }

  public ngOnInit(): void {
    this.getData();
  }

  public ngAfterViewInit(): void {
    this.setEvaluationScoreCopy();
    merge(
      this.evalDropdownTpl.attach,
      this.evalDropdownTpl.detach,
      this.state$.pipe(distinctUntilKeyChanged('hover')),
    )
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(() => this.setEvaluationScoreCopy());
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.candidate || changes.currentUserAvailablePlacements) {
      this.setState({
        ...this.state,
        offers: OfferSelectOptionHelper.placementsToOfferSelectOptions(
          this.currentUserAvailablePlacements,
        ),
        myLastRating: this.candidate?.myLastRating,
      });
    }
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public handleClick(): void {
    if (this.hasQuickEvaluationDropdown) {
      this.openDropdown();
    } else {
      this.optionsClick.emit();
    }
  }

  public hasMyLastRating(): boolean {
    return this.state.myLastRating !== null;
  }

  public onMouseEnter(): void {
    this.setState({
      ...this.state,
      hover: true,
    });
  }

  public onMouseLeave(): void {
    this.setState({
      ...this.state,
      hover: false,
    });
  }

  public hasRating(candidate: FullCandidate): boolean {
    return candidate.positiveRatings !== null || candidate.ratingVisible === false;
  }

  public onSubmit({
    event,
  }: {
    event: {
      type: string;
      value: { talentPoolId?: number; offerId?: number; templateId: number; admins: User[] };
    };
  }): void {
    switch (event.type) {
      case 'addEvaluation': {
        this.analyticsWorkflowTemplates.trackTemplateInsertion(
          WorkflowTemplatesFeatures.EvaluationForms,
        );
        this.createInterviewResult(event.value);
        break;
      }
      case 'addInterview': {
        this.analyticsWorkflowTemplates.trackTemplateInsertion(
          WorkflowTemplatesFeatures.EvaluationForms,
        );
        this.evaluationsAnalytics.trackRequestEvaluation();

        this.appNavigator.openCandidateDialog(this.candidate.id, {
          openInterview: true,
          interviewOfferId: event.value.offerId || event.value.talentPoolId,
          interviewTemplateId: event.value.templateId,
        });
        break;
      }
      case 'addInterviewRequest': {
        this.evaluationsAnalytics.trackRequestEvaluation();
        this.onAddInterviewRequest({ ...event.value });
        break;
      }
      default:
        return;
    }
  }

  public openDropdown(evalDropdown: ConnectedOverlayDirective = this.evalDropdownTpl): void {
    evalDropdown.origin = this.evalDropdownOrigin || this.defaultEvalDropdownTrigger;
    evalDropdown.open = true;
    evalDropdown.detach.pipe(first()).subscribe(() => this.cdRef.detectChanges());
    this.evalDropdownOpen.emit();
  }

  private getData(): void {
    this.store
      .select(InterviewTemplateSelectors.entities({ scope: InterviewTemplateScope.Interview }))
      .pipe(filterBoolean, takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(templates => {
        this.setState({
          ...this.state,
          templates: this.mapTemplates(templates),
        });
      });
  }

  private mapTemplates(templates: InterviewTemplate[]): SelectGroupedOptions[] {
    const groups = templates
      .sort(
        sortByKey('category', {
          direction: 'desc',
          modifyFn: (category: string) => category && category.toLowerCase(),
        }),
      )
      .reduce<Record<string, SelectGroupOption[]>>((acc, template) => {
        const category = template.category || this.i18n.translate('app.common.other');
        if (!acc[category]) {
          acc[category] = [];
        }
        acc[category].push({ label: template.name, value: template.id! });
        return acc;
      }, {});
    return Object.entries(groups).map(([title, options]) => ({ title, options }));
  }

  private getStageId(placement: Placement | undefined): number | null {
    const stageId = placement && placement.stage && placement.stage.id;

    return placement && placement.offer && placement.offer.kind === 'job' && stageId
      ? stageId
      : null;
  }

  private findPlacement(
    offerId: number | null = null,
    talentPoolId: number | null = null,
  ): Placement | undefined {
    return ((this.candidate && this.candidate.placements) || []).find((placement: Placement) =>
      offerId ? placement.offerId === offerId : placement.talentPoolId === talentPoolId,
    );
  }

  private createInterviewResult(value: Partial<InterviewResult>): void {
    const data = {
      ...value,
      kind: InterviewKind.QUICK,
    };
    const placement = this.findPlacement(data.offerId, data.talentPoolId);

    data.stageId = <number>this.getStageId(placement);
    this.store.dispatch(
      InterviewResultsActions.createOnePessimistic({
        payload: {
          data: {
            value: data,
            candidateId: this.candidate.id,
          },
          correlationId: generateUuid(),
        },
      }),
    );
  }

  private createInterviewRequest(value: {
    talentPoolId?: number;
    offerId?: number;
    templateId: number;
    admins: User[];
    shareOnTheGoRequestPayload?: ShareOnTheGoRequestPayload;
  }): void {
    const placement = this.findPlacement(value.offerId);
    const stageId = this.getStageId(placement);
    const formBody = {
      interviewTemplateId: value.templateId,
      talentPoolId: value.talentPoolId,
      offerId: value.offerId,
      stageId: stageId,
    };

    const interviewResultRequestsData = value.admins.map(admin => ({
      ...formBody,
      adminId: admin.id,
    }));
    this.store.dispatch(
      InterviewResultRequestActions.createOnePessimistic({
        payload: {
          candidateId: this.candidate.id,
          data: interviewResultRequestsData,
          shareOnTheGoRequestPayload: value.shareOnTheGoRequestPayload,
          correlationId: generateUuid(),
        },
      }),
    );
  }

  // eslint-disable-next-line max-lines-per-function
  private onAddInterviewRequest(value: {
    talentPoolId?: number;
    offerId?: number;
    templateId: number;
    admins: User[];
  }) {
    this.shareOnTheGoService
      .openShareOnTheGoDialogIfPossible({
        shareOnTheGoDialogData: {
          talentPoolId: value.talentPoolId,
          offerId: value.offerId,
          candidateId: this.candidate.id,
          usersToShareWith: value.admins,
          disableSubmitOnSkipAll: true,
        },
        viewContainerRef: this.evalDropdownTpl.viewContainerRef,
      })
      .pipe(
        filterBoolean,
        switchMap((shareOnTheGoResult: ShareOnTheGoDialogResult) =>
          this.shareOnTheGoService.resultToRequestPayload({
            shareOnTheGoResult,
            offerId: value.offerId,
            talentPoolId: value.talentPoolId,
            candidateId: this.candidate.id,
          }),
        ),
        takeUntil(this.ngOnDestroy$),
      )
      .subscribe(shareOnTheGoPayload => {
        this.createInterviewRequest({
          ...value,
          shareOnTheGoRequestPayload: shareOnTheGoPayload,
          admins: this.filterUsersToRequestEvalFrom({
            users: value.admins,
            offerId: value.offerId,
            talentPoolId: value.talentPoolId,
            shareOnTheGoPayload,
          }),
        });
      });
  }

  private filterUsersToRequestEvalFrom({
    users,
    shareOnTheGoPayload,
    offerId,
    talentPoolId,
  }: {
    users: User[];
    shareOnTheGoPayload: ShareOnTheGoRequestPayload;
    offerId?: number;
    talentPoolId?: number;
  }): User[] {
    return users.filter(
      requestEvalUser =>
        shareOnTheGoPayload.grantAccess.find(
          grantAccessEntry => grantAccessEntry.adminId === requestEvalUser.id,
        ) ||
        requestEvalUser.roleAbilities.includes(Ability.ViewAllJobs) ||
        (offerId && requestEvalUser.offerIds?.includes(offerId)) ||
        (talentPoolId && requestEvalUser.talentPoolIds?.includes(talentPoolId)) ||
        requestEvalUser.accessiblePlacementIds?.includes(this.findPlacement(offerId)?.id ?? -1),
    );
  }

  private setEvaluationScoreCopy() {
    let translationKey = '';
    if (this.state.hover || this.evalDropdownTpl.open) {
      if (this.hasNewCandidateProfile && this.hasMyLastRating()) {
        translationKey = 'app.interview.click_to_evaluate_again';
      }
      if (!this.hasNewCandidateProfile || !this.candidate.ratingVisible) {
        translationKey = 'app.interview.click_to_evaluate';
      }
    } else {
      if (
        !this.hasMyLastRating() &&
        (!this.hasNewCandidateProfile || !this.candidate.ratingVisible)
      ) {
        translationKey = 'app.interview.evaluation_score';
      }
    }

    if (translationKey) {
      this.evaluationScoreCopy = this.i18n.translate(translationKey);
      this.cdRef.detectChanges();
    }
  }
}
